/* eslint-disable react-hooks/exhaustive-deps */
import { Badge, Button, Stack, styled } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import { DateTime } from 'luxon';
import { FC, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { createHourSelectOption, DateTimeRange, DateTimeRangeSelect, Icon, RangeControl, RangeSelect, useBoolFlag } from '../../components';
import { BottomNavigationAction } from '../../components/bottom-navigation';
import { CustomRange, DateTimeRangePicker, RangeSelectValues } from '../../components/date-time-range-picker';
import { FilterBar, FilterElement } from '../../components/filter';
import { FilterToolbar } from '../../components/filter/filter-toolbar';
import { ALERT_LIST_RANGE_KEY } from '../../constants';
import { useSmallScreen } from '../../hooks';
import { getLocalStorageItem, setLocalStorageItem } from '../../util';
import { DateTimeRangePickerProvider } from '../../util/data-time-range-picker';
import { LayoutDesktop } from '../list/layout-desktop';
import { LayoutMobile } from '../list/layout-mobile';
import { useSearch } from '../list/use-search';
import { FilterDrawerTabs } from './filter-drawer-tabs';
import { AlertTabKey, Props } from './view.types';

export const AlertListPageView: FC<Props> = ({
  actions,
  alertTabFilters,
  appliedFilters,
  dateOptions,
  dateRange,
  defaultDateLabel,
  defaultDateRange,
  filterDrawerComponent,
  icon,
  listContent,
  onDateRangeChanged,
  onFiltersChanged,
  openDateRange,
  openFilter,
  removeAllFilters,
  removeFilter,
  setDateRangeOpen,
  setFilterOpen,
  showFilterButton = true,
  subtitle,
  title,
  ...props
}): JSX.Element => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchDropRef = useRef<HTMLDivElement>(null);
  const smallScreen = useSmallScreen();
  const { setSavedSelection } = useSearch();
  const { t } = useTranslation(['common']);
  const [rangeSelection, setRangeSelection] = useState<RangeSelectValues['key']>(
    () => getLocalStorageItem(ALERT_LIST_RANGE_KEY, {
      rangeSelection: RangeSelectValues.LAST_12_HOURS.key,
    }).rangeSelection);
  const [isDateTimePickerOpen, setIsDateTimePickerOpen] = useState(false);
  const [savedRange, setSavedRange] = useState<CustomRange>({
    startDate: dateRange?.startTime,
    endDate: dateRange?.endTime,
  });
  const useDateTimeRangeSelectComponent = useBoolFlag('track-alerts-list-datetime-picker-component-temporary');

  const datePickerComponent = (
    <DateTimeRangePickerProvider>
      <DateTimeRangePicker
        data-testid='alerts-picker'
        closeDateTimePicker={() => setIsDateTimePickerOpen(false)}
        isDateTimePickerOpen={isDateTimePickerOpen}
        onDateRangeChanged={onDateRangeChanged}
        savedRange={savedRange}
        setRangeSelection={setRangeSelection}
        setSavedRange={setSavedRange}
      />
    </DateTimeRangePickerProvider>
  );

  const actionComponents = useMemo(() => {
    if (!actions?.length) return <></>;
    return actions.map((action, i) => (
      <Stack
        key={i}
        direction="row"
        spacing={smallScreen ? 0 : 2}
        alignItems="center" width={smallScreen ? '100%' : undefined}
      >
        {action.info}
        <Box sx={action.sx}>
          <Button
            data-testid={action.testId ?? 'action-button'}
            onClick={action.onClick}
            startIcon={action.icon}
          >
            {action.label}
          </Button>
        </Box>
      </Stack>
    ));
  }, [actions, smallScreen]);

  const defaultFilterDrawerComponent = <>
    <FilterToolbar onCloseClicked={() => setFilterOpen(false)} />
    <FilterDrawerTabs
      alertTabFilters={alertTabFilters}
      onCloseClicked={() => setFilterOpen(false)}
      setTabFilters={onFiltersChanged}
      tabsFilters={appliedFilters}
    />
  </>;

  const dateComponent = useMemo(() => (
    useDateTimeRangeSelectComponent
      ? <DateTimeRangeSelect
        smallScreen={smallScreen}
        onDateChange={(startDate, endDate) => {
          onDateRangeChanged(startDate.toJSDate(), endDate.toJSDate());
        }}
        quickSelectOptions={[
          {
            label: t('common:page.alert-list.date-quick-select-today.labels'),
            value: DateTime.now().startOf('day'),
          },
          {
            label: t('common:page.alert-list.date-quick-select-week.labels'),
            value: DateTime.now().startOf('week'),
          },
          {
            label: t('common:page.alert-list.date-quick-select-month.labels'),
            value: DateTime.now().startOf('month'),
          },
        ]}
        value={dateRange
          ? {
            startDate: dateRange.startTime,
            endDate: dateRange.endTime,
          }
          : undefined
        }
        defaultValue={{
          startDate: defaultDateRange.startTime,
          endDate: defaultDateRange.endTime,
        }}
      />
      : <DateTimeRange
        dateOptions={dateOptions}
        placeholderText={t('common:common.labels.date-range')}
        defaultStart={defaultDateRange.startTime}
        defaultLabel={defaultDateLabel}
        defaultEnd={defaultDateRange.endTime}
        onChange={onDateRangeChanged}
        size="small"
        sx={{
          width: '100%',
          '& .MuiInputLabel-root': { py: '0.15rem' },
          '& .MuiOutlinedInput-input': { px: '1rem', py: '0.657rem' },
        }}
      />
  ), [dateRange, defaultDateRange.endTime, defaultDateRange.startTime, smallScreen]);

  const rangeSelectOptions = [
    createHourSelectOption(RangeSelectValues.LAST_HOUR),
    createHourSelectOption(RangeSelectValues.LAST_12_HOURS),
    createHourSelectOption(RangeSelectValues.LAST_24_HOURS),
  ];

  const rangeSelectComponent = (
    <Box sx={{ minHeight: '85px' }}>
      {useDateTimeRangeSelectComponent
        ? dateComponent
        : <>
          <RangeSelect
            data-testid='alert-range-select'
            ref={searchDropRef}
            isDateTimePickerOpen={isDateTimePickerOpen}
            onDateRangeChanged={onDateRangeChanged}
            openDateTimePicker={() => setIsDateTimePickerOpen(true)}
            rangeSelection={rangeSelection}
            rangeSelectOptions={rangeSelectOptions}
            savedRange={savedRange}
            setRangeSelection={setRangeSelection}
          />
          <RangeControl
            onDateRangeChanged={onDateRangeChanged}
            renderControl={rangeSelection === RangeSelectValues.PICK_CUSTOM_RANGE.key}
            savedRange={savedRange}
            setSavedRange={setSavedRange}
          />
        </>
      }
    </Box>
  );

  const tabKeys = Object.keys(alertTabFilters) as (AlertTabKey)[];
  const hasFilters = tabKeys.some((key) => !!appliedFilters[key].length);
  const filtersLength = tabKeys
    .map((key) => appliedFilters[key].length)
    .reduce((acc, item) => acc += item, 0);

  const appliedFiltersComponents = useMemo(
    () => !smallScreen && hasFilters
      ? <>
        {tabKeys.map((key) =>
          appliedFilters[key].map((filter) =>
            <FilterElement
              key={filter.id}
              filter={filter}
              onRemoveClicked={() => removeFilter(filter, key)}
              sx={{ my: .5 }}
            />,
          ))}
      </>
      : undefined,
    [appliedFilters, smallScreen],
  );

  const ClearFiltersButton = styled(Button)(({ theme }) => ({ marginRight: theme.spacing(1) }));
  const clearAllFiltersButton = (
    <ClearFiltersButton
      data-testid="clear-filters-button"
      onClick={removeAllFilters}
      startIcon={<Icon data-testid="clear-icon" icon="CloseIcon" />}
      variant="text"
    >
      {t('common:component.filter.actions.clear')}
    </ClearFiltersButton>
  );

  const filterIcon = (
    <Badge content={filtersLength}>
      <Icon data-testid="filter-icon" icon="FilterIcon" />
    </Badge>
  );

  const filterButtonComponent = (
    <FilterBar
      filtersLength={filtersLength}
      onFilterClicked={() => setFilterOpen(!openFilter)}
    />
  );

  useEffect(() => {
    setSavedSelection?.(rangeSelection);
    setLocalStorageItem(ALERT_LIST_RANGE_KEY, { rangeSelection });
  }, [rangeSelection]);

  if (smallScreen) {
    return (
      <LayoutMobile
        actions={[
          <BottomNavigationAction key={'dateRange'} label={t('common:common.labels.date-range')} icon={dateComponent} data-testid="action-button-date-range" />,
          <BottomNavigationAction
            key={'filter'}
            data-testid="action-button-filter"
            icon={filterIcon}
            label={t('common:terms.filter')}
            onClick={() => setFilterOpen(true)}
          />,
        ]}
        clearAllFiltersButton={clearAllFiltersButton}
        content={listContent}
        data-testid={props['data-testid']}
        filterContent={filterDrawerComponent ?? defaultFilterDrawerComponent}
        filters={appliedFiltersComponents}
        pagination={undefined}
        primaryActions={actionComponents}
        scrollRef={scrollRef}
        search={<></>}
        searchOpen={openDateRange}
        setSearchOpen={setDateRangeOpen}
        subtitle={subtitle}
        showFilters={openFilter}
      />
    );
  }

  return (
    <LayoutDesktop
      actions={actionComponents}
      clearAllFiltersButton={clearAllFiltersButton}
      content={listContent}
      data-testid={props['data-testid']}
      datePicker={datePickerComponent}
      filterButton={filterButtonComponent}
      showFilterButton={showFilterButton}
      filterContent={filterDrawerComponent ?? defaultFilterDrawerComponent}
      filters={appliedFiltersComponents}
      icon={icon}
      pagination={undefined}
      scrollRef={scrollRef}
      search={rangeSelectComponent}
      setFilterOpen={setFilterOpen}
      showFilters={openFilter}
      title={title}
      subtitle={subtitle}
    />
  );
};
